import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
//#region types
import { TranslationKeys } from "translations";
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { WintSuperkollAPIModelTimeReporting } from "wint-api-client/dist/types";
//#endregion
//#region components
import {
  PageLayouts,
  SimpleEntityTitle,
  useMountedState,
  BreadCrumb,
  useNavigationHistory,
  WizardContextProvider,
  FormValidity,
  LoadingWithLogo,
} from "components/common";
import useCloseConfirmation from "components/common/dialogs/useCloseConfirmation";
import CreateTimeReportingProjectFooter from "./createFooter";
import TimeReportingProjectFormNavWrapper from "./TimeReportingProjectFormNavWrapper";
import { useTimeReportingProjectService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import { FormHandles } from "models/propTypes/genericFormProps";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

const TimeReportingProjectSettings: React.FC = () => {
  const timeReportingProjectService = useTimeReportingProjectService();
  const urlParams = useParams<EntityIdParams>();
  const id = urlParams.id!;
  const { getMounted } = useMountedState();
  const [selectedTimeReportingProjectName, setSelectedTimeReportingProjectName] = useState<string>("");
  const [timeReportingProject, setTimeReportingProject] =
    useState<WintSuperkollAPIModelTimeReporting.TimeReportingProject>();

  const [loading, setLoading] = React.useState<boolean>(true);

  const formRef = useRef<FormHandles>(null);
  const { navigateToPreviousLocation, goBack } = useNavigationHistory();
  const { showConfirmation } = useCloseConfirmation();

  const isFormDirty = () => {
    if (formRef.current?.isDirty) return formRef.current.isDirty();
    const touched = formRef.current?.getTouched && formRef.current.getTouched();
    return !!touched && Object.keys(touched).length > 0;
  };

  const handleCloseRequest = () => {
    if (isFormDirty()) {
      showConfirmation(async () => goBack());
    } else {
      goBack();
    }
  };

  React.useEffect(() => {
    loadProjectData();
  }, [id, getMounted]);

  const loadProjectData = () => {
    setLoading(true);
    timeReportingProjectService
      .getTimeReportingProject(id)
      .then((result) => {
        if (getMounted()) {
          setTimeReportingProject(result);
          setSelectedTimeReportingProjectName(result?.name ?? "");
        }
      })
      .then((res) => {
        setLoading(false);
      });
  };

  const onSubmit = () => {
    return (formRef.current && formRef.current.submit()) || new Promise<void>((resolve) => resolve());
  };

  const onFormSubmitSuccess = (timeReportingProjectId?: string) => {
    loadProjectData();
    navigateToPreviousLocation({
      timeReportingProjectId: timeReportingProjectId,
      timeReportingProjectName: formRef.current?.getValues && formRef.current.getValues().name,
    });
  };

  return (
    <PageLayouts.EntityFullScreen
      id={0}
      entityKey={`timeReportingProject`}
      navigation={
        <BreadCrumb
          prevItems={[
            {
              text: LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.TimeReportingProject.TimeReportingProjects
              ),
              href: UrlHelper.formatPath("/Reports/Projects"),
            },
            { text: selectedTimeReportingProjectName, href: UrlHelper.formatPath(`/Projects/${id}/`) },
          ]}
          current={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Settings)}
          showHome
        />
      }
      hasPermissions={timeReportingProjectService.hasEditPermissions()}
      hideSpeedDial
      closeDialog={handleCloseRequest}
      confirmCloseOnX
    >
      {loading ? (
        <LoadingWithLogo />
      ) : (
        <WizardContextProvider>
          <FormValidity
            render={(isValid, onValidityChange) => (
              <PageLayouts.EntityCreationLayout
                className={clsx(cssExports.projectSettingsWizard, cssExports.settingsWizardIcons)}
                upperLeftComponent={<SimpleEntityTitle title={selectedTimeReportingProjectName} />}
                upperRightComponent={<></>}
                footer={
                  <CreateTimeReportingProjectFooter
                    submitForm={onSubmit}
                    onSubmitSuccess={onFormSubmitSuccess}
                    isFormValid={isValid}
                  />
                }
              >
                <TimeReportingProjectFormNavWrapper
                  ref={formRef}
                  selectedTimeReportingProjectChangeHandler={setSelectedTimeReportingProjectName}
                  selectedTimeReportingProjectName={selectedTimeReportingProjectName ?? ""}
                  timeReportingProject={timeReportingProject}
                  onFormValidationChange={onValidityChange}
                />
              </PageLayouts.EntityCreationLayout>
            )}
          />
        </WizardContextProvider>
      )}
    </PageLayouts.EntityFullScreen>
  );
};

export default TimeReportingProjectSettings;
